import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LifeBuoy } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';
import SupportModal from '../SupportModal';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const TicketButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const { user } = useAuthStore();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50;
    
    const checkWidget = setInterval(() => {
      if (window.FreshworksWidget) {
        setWidgetLoaded(true);
        clearInterval(checkWidget);
      }
      
      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(checkWidget);
      }
    }, 100);

    return () => clearInterval(checkWidget);
  }, []);

  const handleProductSelect = (product: 'theme' | 'manager', shopifyUrl?: string) => {
    if (!widgetLoaded) {
      toast.error('Le widget de support est en cours de chargement. Veuillez réessayer dans quelques secondes.');
      return;
    }

    if (!window.FreshworksWidget) {
      toast.error('Le widget de support n\'est pas disponible. Veuillez réessayer plus tard.');
      return;
    }

    if (!user?.email) {
      toast.error('Vous devez être connecté pour créer un ticket.');
      return;
    }

    try {
      // Définir les champs à désactiver
      let fieldsToDisable = ['email', 'custom_fields.cf_produit_concern', 'custom_fields.cf_url_shopify'];
  
      // Désactiver "Version du thème" uniquement si l'utilisateur sélectionne "App Shopiweb Manager"
      if (product === 'manager') {
        fieldsToDisable.push('custom_fields.cf_version', 'custom_fields.cf_autorisation_daccs274321', 'custom_fields.cf_code_collaborateur');
      }
  
      window.FreshworksWidget('disable', 'ticketForm', fieldsToDisable);
      
      // Pré-remplir les informations de l'utilisateur
      window.FreshworksWidget('prefill', 'ticketForm', {
        name: user.displayName || user.email.split('@')[0] || '',
        email: user.email,
        custom_fields: {
          cf_produit_concern: product === 'theme' ? 'Thème Shopiweb Premium' : 'App Shopiweb Manager',
          cf_url_shopify: shopifyUrl || '' // Correction du nom du champ
        }
      });

      // Fermer le modal de sélection
      setIsOpen(false);

      // Afficher et ouvrir le widget
      window.FreshworksWidget('show', 'launcher');
      window.FreshworksWidget('open');
    } catch (error) {
      console.error('Error opening Freshworks widget:', error);
      toast.error('Une erreur est survenue lors de l\'ouverture du widget de support.');
    }
  };

  // Hide button on mobile
  if (isMobile) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-20 right-4 z-40">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="relative p-4 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors"
          aria-label="Ouvrir le formulaire de support"
        >
          <LifeBuoy className="w-6 h-6" />
          
          {/* Tooltip */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg whitespace-nowrap"
              >
                Créer un ticket de support
                <div className="absolute bottom-0 right-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700"></div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <SupportModal
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onProductSelect={handleProductSelect}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TicketButton;